import RootSetup from '@/setup';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import * as Sentry from '@sentry/react';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import AlertsList from 'components/AlertsList';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { StompSessionProvider } from 'react-stomp-hooks';
import { LockIcon, UserPermission } from 'remote/global';
import AccountInvitationRegistrationView from 'views/AccountInvitationRegistrationView';
import AlertsView from 'views/AlertsView';
import AnalyticsView from 'views/AnalyticsView';
import AvailabilityView from 'views/AvailabilityView/AvailabilityView';
import DriverSchedulerView from 'views/AvailabilityView/DriverSchedulerView';
import VehicleScedulerView from 'views/AvailabilityView/VehicleSchedulerView';
import B2BMyClientsView from 'views/B2BView/B2BMyClientsView';
import B2BView from 'views/B2BView/B2BView';
import DashboardView from 'views/DashboardView/DashboardView';
import DocumentationRedirectView from 'views/DocumentationRedirectView';
import DriverDetailView from 'views/DriversView/DriverDetailView';
import DriverDocumentsView from 'views/DriversView/DriverDocumentsView';
import DriversDetailView from 'views/DriversView/DriversDetailView';
import DriversView from 'views/DriversView/DriversView';
import DriverVacationsView from 'views/DriversView/DriverVacationsView';
import FleetView from 'views/FleetView/FleetView';
import VehicleDetailsView from 'views/FleetView/VehicleDetailsView';
import VehicleDetailView from 'views/FleetView/VehicleDetailView';
import VehicleDocumentsView from 'views/FleetView/VehicleDocumentsView';
import VehicleLocationsView from 'views/FleetView/VehicleLocationsView';
import VehicleRepairLogView from 'views/FleetView/VehicleRepairLogView';
import LoggedInContainer from 'views/LoggedInContainer';
import LoginView from 'views/LoginView';
import LogisticsAllRidesView from 'views/LogisticsView/LogisticsAllRidesView';
import LogisticsMapView from 'views/LogisticsView/LogisticsMapView';
import LogisticsView from 'views/LogisticsView/LogisticsView';
import MessagesGroupEditView from 'views/MessagesView/MessagesGroupEditView';
import MessagesGroupsView from 'views/MessagesView/MessagesGroupsView';
import MessagesHistoryView from 'views/MessagesView/MessagesHistoryView';
import MessagesView from 'views/MessagesView/MessagesView';
import OrdersDetailView from 'views/OrdersDetailView';
import PricingOpCostsView from 'views/PricingView/PricingOpCostsView';
import PricingPeriodsView from 'views/PricingView/PricingPeriodsView';
import PricingView from 'views/PricingView/PricingView';
import RedirectView from 'views/RedirectView';
import ResetPasswordTokenView from 'views/ResetPasswordTokenView';
import SettingsAccountView from 'views/SettingsView/SettingsAccountView';
import SettingsCompanyDetailsView from 'views/SettingsView/SettingsCompanyDetailsView';
import SettingsSubscriptionDetailsView from 'views/SettingsView/SettingsSubscriptionDetailsView';
import SettingsUsersView from 'views/SettingsView/SettingsUsersView';
import SettingsView from 'views/SettingsView/SettingsView';
import SignUpView from 'views/SignUpView';
import { CookieKeyEnum } from '../data/enums';
import { ROUTES } from '../data/routes';
import { isDev } from '../helpers';

// TODO: Add <Navigate /> fallback for parent routes that find no match within child routes

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const getLivemapUrl = () => {
  if (isDev) {
    return 'wss://be.dev.mobifer.com/livemap';
  }
  return 'wss://be.mobifer.com/livemap';
};
const getAlertCountUrl = () => {
  if (isDev) {
    return 'wss://be.dev.mobifer.com/websocket-events';
  }
  return 'wss://be.mobifer.com/websocket-events';
};

function RootGate() {
  const { isSuccess: isAuthenticated } = useUsersCurrentAccessesQuery();
  const location = useLocation();

  if (!isAuthenticated)
    return (
      <Navigate
        to={ROUTES.BaseRoutes.LOGIN}
        {...('state' in location && { state: location.state })}
      />
    );

  return <Navigate to={location.state?.from ?? ROUTES.BaseRoutes.MAIN} />;
}

function PermissionsGate({ role }: { role: string }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const outletContext = useOutletContext(); // Context only works with immediate children, pass it on
  if ([UserPermission.NO_ACCESS, UserPermission.RESTRICTED].includes(role as UserPermission))
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
          textAlign: 'center',
          backgroundColor: 'rgba(31, 34, 50, 0.5)',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#fff',
            pt: 4,
            pb: 4,
            pr: 5,
            pl: 5,
            borderRadius: 2,
            boxShadow: 3,
            maxWidth: '421px',
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'inline-block' }}>
            <LockIcon />
          </div>

          <Typography
            sx={{
              m: 3,

              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '120%',
            }}
          >
            {role === UserPermission.NO_ACCESS && t('ForbiddenView.title')}
            {role === UserPermission.RESTRICTED && t('RestrictedView.title')}
          </Typography>
          <Typography variant='body2' mb={3} maxWidth='350px'>
            {role === UserPermission.NO_ACCESS && t('ForbiddenView.subtitle')}
            {role === UserPermission.RESTRICTED && t('RestrictedView.subtitle')}
          </Typography>
          <Button
            variant='contained'
            color='primary'
            fullWidth
            onClick={() => {
              if (role === UserPermission.NO_ACCESS) navigate(-1);
              if (role === UserPermission.RESTRICTED) navigate('/main/settings/subscription');
            }}
          >
            {role === UserPermission.NO_ACCESS && t('actions.fetch.goBack')}
            {role === UserPermission.RESTRICTED && t('ForbiddenView.goToAccountSettings')}
          </Button>
        </Box>
      </Box>
    );

  return <Outlet context={outletContext} />;
}

// If the user is authorized, then they are not allowed to navigate to this Route
// And should be navigated to RootDecider
function PublicAuthorizationGate() {
  const outletContext = useOutletContext(); // Context only works with immediate children, pass it on
  const { isSuccess: isAuthenticated } = useUsersCurrentAccessesQuery();
  const location = useLocation();

  if (isAuthenticated) return <Navigate to={location.state?.from ?? ROUTES.BaseRoutes.MAIN} />;
  return <Outlet context={outletContext} />;
}

function NotFoundGate() {
  const { isSuccess: isAuthenticated } = useUsersCurrentAccessesQuery();
  const location = useLocation();

  if (isAuthenticated) return <Navigate to={ROUTES.BaseRoutes.MAIN} replace />;
  return <Navigate to={ROUTES.BaseRoutes.ROOT} state={{ from: location.pathname }} replace />;
}

// Used to wrap the entire 'authenticated' routes for auth check
function ProtectedRouteGate() {
  const outletContext = useOutletContext(); // Context only works with immediate children, pass it on
  const { isSuccess: isAuthenticated, isLoading } = useUsersCurrentAccessesQuery();
  const location = useLocation();

  if (isLoading) return null; // Prevents redirect when auth is loading

  if (!isAuthenticated)
    return <Navigate to={ROUTES.BaseRoutes.LOGIN} state={{ from: location.pathname }} />;

  return <Outlet context={outletContext} />;
}

const useAppRouter = () => {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const { permissions = {} } = userInfo ?? {};

  const jwtCookie = Cookies.get(CookieKeyEnum.JWT_TOKEN);

  const routes = [
    <Route path={ROUTES.BaseRoutes.ROOT} element={<RootGate />} key={ROUTES.BaseRoutes.ROOT} />,
    <Route element={<PublicAuthorizationGate />} key='public-gate'>
      <Route path={ROUTES.BaseRoutes.LOGIN} element={<LoginView />} key={ROUTES.BaseRoutes.LOGIN} />
      <Route
        path={ROUTES.BaseRoutes.PASSWORD_RESET}
        element={<ResetPasswordTokenView />}
        key={ROUTES.BaseRoutes.PASSWORD_RESET}
      />
      <Route
        path={ROUTES.BaseRoutes.SIGNUP}
        element={<SignUpView />}
        key={ROUTES.BaseRoutes.SIGNUP}
      >
        <Route path=':accessToken' element={<SignUpView />} />
      </Route>
      <Route
        path={ROUTES.BaseRoutes.INVITATION}
        element={<AccountInvitationRegistrationView />}
        key={ROUTES.BaseRoutes.INVITATION}
      />
    </Route>,
    <Route
      path={ROUTES.BaseRoutes.REDIRECT}
      element={<RedirectView />}
      key={ROUTES.BaseRoutes.REDIRECT}
    />,

    <Route key='protected-route-root' element={<ProtectedRouteGate />}>
      <Route
        path={ROUTES.BaseRoutes.MAIN}
        key={ROUTES.BaseRoutes.MAIN}
        element={
          <StompSessionProvider
            url={getAlertCountUrl()}
            connectHeaders={{
              'X-Auth-Token': jwtCookie as string,
            }}
            reconnectDelay={0}
            connectionTimeout={60000}
          >
            <LoggedInContainer />
          </StompSessionProvider>
        }
      >
        <Route
          index
          key='index-main'
          element={<Navigate to={ROUTES.MainRoutes.DASHBOARD} replace />}
        />
        <Route
          key={ROUTES.MainRoutes.DASHBOARD}
          path={ROUTES.MainRoutes.DASHBOARD}
          element={<DashboardView />}
        />
        <Route path={ROUTES.MainRoutes.ORDERS} key={ROUTES.MainRoutes.ORDERS}>
          <Route index element={<Navigate to='/' replace />} />
          <Route path=':id' element={<OrdersDetailView />} key='orders-detail-view' />
        </Route>
        <Route
          key={ROUTES.MainRoutes.AVAILABILITY}
          path={ROUTES.MainRoutes.AVAILABILITY}
          element={<AvailabilityView />}
        >
          <Route
            key='index-availability'
            index
            element={<Navigate to={ROUTES.AvailabilityRoutes.BUS} replace />}
          />
          <Route
            key='bus-schedule'
            element={<PermissionsGate role={permissions.VEHICLE_SCHEDULE} />}
          >
            <Route
              key={ROUTES.AvailabilityRoutes.BUS}
              path={ROUTES.AvailabilityRoutes.BUS}
              element={<VehicleScedulerView />}
            />
            ,
          </Route>
          <Route
            key='bus-schedule'
            element={<PermissionsGate role={permissions.DRIVER_SCHEDULE} />}
          >
            <Route
              key='driver-schedule'
              path={ROUTES.AvailabilityRoutes.DRIVER}
              element={<DriverSchedulerView />}
            />
            ,
          </Route>
          <Route
            key='availability-wildcard'
            path='*'
            element={<Navigate to={ROUTES.AvailabilityRoutes.BUS} replace />}
          />
        </Route>
        <Route
          key={ROUTES.MainRoutes.OFFERS}
          path={ROUTES.MainRoutes.OFFERS}
          element={<PermissionsGate role={permissions.COMPETING_OFFERS} />}
        >
          <Route key='offers-index' index element={<AnalyticsView />} />
        </Route>
        <Route
          key='fleet-schedule'
          element={<PermissionsGate role={permissions.VEHICLE_SCHEDULE} />}
        >
          <Route key={ROUTES.MainRoutes.FLEET} path={ROUTES.MainRoutes.FLEET}>
            <Route key='fleet-index' index element={<FleetView />} />
            <Route
              key='fleet-details'
              element={<PermissionsGate role={permissions.VEHICLE_DETAILS} />}
            >
              <Route key='vehicle-details' path=':id' element={<VehicleDetailsView />}>
                <Route key='vehicle-details-index' index element={<VehicleDetailView />} />
                <Route
                  key='vehicle-location'
                  element={<PermissionsGate role={permissions.VEHICLE_LOCATION} />}
                >
                  <Route
                    key={ROUTES.FleetRoutes.LOCATION}
                    path={ROUTES.FleetRoutes.LOCATION}
                    element={<VehicleLocationsView />}
                  />
                </Route>
                <Route
                  key='vehicle-file'
                  element={<PermissionsGate role={permissions.VEHICLE_FILE} />}
                >
                  <Route
                    key={ROUTES.FleetRoutes.DOCUMENTS}
                    path={ROUTES.FleetRoutes.DOCUMENTS}
                    element={<VehicleDocumentsView />}
                  />
                </Route>
                <Route
                  key={ROUTES.FleetRoutes.REPAIR}
                  path={ROUTES.FleetRoutes.REPAIR}
                  element={<VehicleRepairLogView />}
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route
          key='driver-schedule-main'
          element={<PermissionsGate role={permissions.DRIVER_SCHEDULE} />}
        >
          <Route key={ROUTES.MainRoutes.DRIVERS} path={ROUTES.MainRoutes.DRIVERS}>
            <Route key='drivers-index' index element={<DriversView />} />
            <Route
              key='driver-details-main'
              element={<PermissionsGate role={permissions.DRIVER_DETAILS} />}
            >
              <Route key='driver-details-id' path=':id' element={<DriversDetailView />}>
                <Route key='driver-details-index' index element={<DriverDetailView />} />
                <Route
                  key='driver-file'
                  element={<PermissionsGate role={permissions.DRIVER_FILE} />}
                >
                  <Route
                    key={ROUTES.DriversRoutes.DOCUMENTS}
                    path={ROUTES.DriversRoutes.DOCUMENTS}
                    element={<DriverDocumentsView />}
                  />
                </Route>
                <Route
                  key={ROUTES.DriversRoutes.VACATION}
                  path={ROUTES.DriversRoutes.VACATION}
                  element={<DriverVacationsView />}
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route
          key={ROUTES.MainRoutes.PRICING}
          path={ROUTES.MainRoutes.PRICING}
          element={<PermissionsGate role={permissions.OPERATOR_PRICING} />}
        >
          <Route key='pricing' element={<PricingView />}>
            <Route
              key='pricing-index'
              index
              element={<Navigate to={ROUTES.PricingRoutes.COST} replace />}
            />
            <Route
              key={ROUTES.PricingRoutes.COST}
              path={ROUTES.PricingRoutes.COST}
              element={<PricingOpCostsView />}
            />
            <Route
              key={ROUTES.PricingRoutes.PERIOD}
              path={ROUTES.PricingRoutes.PERIOD}
              element={<PricingPeriodsView />}
            />
          </Route>
        </Route>
        <Route
          key={ROUTES.MainRoutes.B2B}
          path={ROUTES.MainRoutes.B2B}
          element={<PermissionsGate role={permissions.OPERATOR_CLIENT} />}
        >
          <Route key='b2b-view' element={<B2BView />}>
            <Route
              key='b2b-index'
              index
              element={<Navigate to={ROUTES.B2BRoutes.MY_CLIENTS} replace />}
            />
            <Route
              key={ROUTES.B2BRoutes.MY_CLIENTS}
              path={ROUTES.B2BRoutes.MY_CLIENTS}
              element={<B2BMyClientsView />}
            />
          </Route>
        </Route>
        <Route key='permissions-gate-rides' element={<PermissionsGate role={permissions.RIDE} />}>
          <Route key={ROUTES.MainRoutes.LOGISTICS} path={ROUTES.MainRoutes.LOGISTICS}>
            <Route key='logistics-view' element={<LogisticsView />}>
              <Route
                key='logistics-index'
                index
                element={<Navigate to={ROUTES.LogisticsRoutes.ALL_RIDES} replace />}
              />
              <Route
                key={ROUTES.LogisticsRoutes.ALL_RIDES}
                path={ROUTES.LogisticsRoutes.ALL_RIDES}
                element={<LogisticsAllRidesView />}
              />
              <Route
                key={ROUTES.LogisticsRoutes.MAP}
                path={ROUTES.LogisticsRoutes.MAP}
                element={
                  <StompSessionProvider
                    url={getLivemapUrl()}
                    connectHeaders={{
                      'X-Auth-Token': jwtCookie as string,
                    }}
                    reconnectDelay={0}
                    connectionTimeout={60000}
                  >
                    <LogisticsMapView />
                  </StompSessionProvider>
                }
              />
            </Route>
          </Route>
        </Route>
        <Route
          key='user-account-gate'
          element={<PermissionsGate role={permissions.USER_ACCOUNT} />}
        >
          <Route key={ROUTES.MainRoutes.SETTINGS} path={ROUTES.MainRoutes.SETTINGS}>
            <Route key='settings-view' element={<SettingsView />}>
              <Route
                key='settings-index'
                index
                element={<Navigate to={ROUTES.SettingsRoutes.ACCOUNT} replace />}
              />
              <Route
                key={ROUTES.SettingsRoutes.ACCOUNT}
                path={ROUTES.SettingsRoutes.ACCOUNT}
                element={<SettingsAccountView />}
              />
              <Route
                key='operator-subscription-gate'
                element={<PermissionsGate role={permissions.OPERATOR_FULL_DETAILS} />}
              >
                <Route
                  key={ROUTES.SettingsRoutes.SUBSCRIPTION}
                  path={ROUTES.SettingsRoutes.SUBSCRIPTION}
                  element={<SettingsSubscriptionDetailsView />}
                />
              </Route>
              <Route
                key='operator-details-gate'
                element={<PermissionsGate role={permissions.OPERATOR_FULL_DETAILS} />}
              >
                <Route
                  key={ROUTES.SettingsRoutes.COMPANY}
                  path={ROUTES.SettingsRoutes.COMPANY}
                  element={<SettingsCompanyDetailsView />}
                />
              </Route>
              <Route
                key='operator-user-gate'
                element={<PermissionsGate role={permissions.OPERATOR_USER} />}
              >
                <Route
                  key={ROUTES.SettingsRoutes.USERS}
                  path={ROUTES.SettingsRoutes.USERS}
                  element={<SettingsUsersView />}
                />
              </Route>
              {/* // Temporarily disabled, will be used in the future */}
              {/* <Route element={<PermissionsGate role={permissions.OPERATOR_FULL_DETAILS} />}>
              <Route
                path={ROUTES.SettingsRoutes.CONFIGURATION}
                element={<SettingsConfigurationView />}
              />
            </Route> */}
            </Route>
          </Route>
        </Route>
        <Route
          key={ROUTES.MainRoutes.DOCUMENTATION}
          path={ROUTES.MainRoutes.DOCUMENTATION}
          element={<DocumentationRedirectView />}
        />
        <Route key='alert-gate' element={<PermissionsGate role={permissions.ALERT} />}>
          <Route
            key={ROUTES.MainRoutes.ALERTS}
            path={ROUTES.MainRoutes.ALERTS}
            element={<AlertsView />}
          >
            <Route
              key='alerts-index'
              index
              element={<Navigate to={ROUTES.AlertsRoutes.CURRENT} replace />}
            />
            <Route
              key={ROUTES.AlertsRoutes.CURRENT}
              path={ROUTES.AlertsRoutes.CURRENT}
              element={<AlertsList />}
            />
            <Route
              key={ROUTES.AlertsRoutes.HISTORY}
              path={ROUTES.AlertsRoutes.HISTORY}
              element={<AlertsList showAlertHistory />}
            />
          </Route>
        </Route>
        <Route
          key='user-message-gate'
          element={<PermissionsGate role={permissions.USER_MESSAGE} />}
        >
          <Route key={ROUTES.MainRoutes.MESSAGES} path={ROUTES.MainRoutes.MESSAGES}>
            <Route key='messages-view' element={<MessagesView />}>
              <Route
                key='messages-index'
                index
                element={<Navigate to={ROUTES.MessagesRoutes.HISTORY} replace />}
              />
              <Route
                key={ROUTES.MessagesRoutes.HISTORY}
                path={ROUTES.MessagesRoutes.HISTORY}
                element={<MessagesHistoryView />}
              />
              <Route
                key='user-message-group-gate'
                element={<PermissionsGate role={permissions.USER_MESSAGE_GROUP} />}
              >
                <Route key={ROUTES.MessagesRoutes.GROUP} path={ROUTES.MessagesRoutes.GROUP}>
                  <Route key='message-group-index' index element={<MessagesGroupsView />} />
                  <Route key='message-group-id' path=':id' element={<MessagesGroupEditView />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>,
    <Route path='*' element={<NotFoundGate />} key='*' />,
  ];

  const router = sentryCreateBrowserRouter(
    createRoutesFromElements([
      <Route key='RootSetup' element={<RootSetup />}>
        {routes}
      </Route>,
    ])
  );

  return router;
};

export default useAppRouter;
